import React, { useEffect, useState } from 'react';
import { auditFetch, buildAuditHeaders } from '../utils/auditFetch';
import '../styles/Dashboard.css';

const orderTypeLabels = {
  laboratorio: 'Laboratorio',
  imagen: 'Imagen',
};

const priorityLabels = {
  rutina: 'Rutina',
  preferente: 'Preferente',
  urgente: 'Urgente',
};

const MedicalOrdersPanel = ({
  appointmentId,
  patient,
  doctor,
  user,
  defaultDiagnosis = '',
}) => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const [orderType, setOrderType] = useState('laboratorio');
  const [requestedStudies, setRequestedStudies] = useState('');
  const [clinicalIndications, setClinicalIndications] = useState('');
  const [priority, setPriority] = useState('rutina');
  const [relatedDiagnosis, setRelatedDiagnosis] = useState(defaultDiagnosis);
  const [orders, setOrders] = useState([]);
  const [statusMessage, setStatusMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setRelatedDiagnosis((current) => current || defaultDiagnosis);
  }, [defaultDiagnosis]);

  useEffect(() => {
    if (!appointmentId) return;
    auditFetch(`${backendUrl}/api/medical-orders/appointment/${appointmentId}`, {}, user)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('No se pudieron cargar ordenes'))))
      .then((data) => setOrders(Array.isArray(data.orders) ? data.orders : []))
      .catch((error) => {
        console.error('Error loading medical orders:', error);
        setStatusMessage('No se pudieron cargar ordenes previas.');
      });
  }, [appointmentId, backendUrl, user]);

  const openPdf = (order) => {
    const url = `${backendUrl}/api/medical-orders/${order.id}/pdf`;
    fetch(url, { headers: buildAuditHeaders(user) })
      .then((res) => {
        if (!res.ok) throw new Error('No se pudo generar el PDF');
        return res.blob();
      })
      .then((blob) => {
        const objectUrl = URL.createObjectURL(blob);
        window.open(objectUrl, '_blank', 'noopener,noreferrer');
        setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
      })
      .catch((error) => {
        console.error('Error opening order PDF:', error);
        setStatusMessage('No se pudo abrir el PDF de la orden.');
      });
  };

  const saveOrder = async () => {
    if (!requestedStudies.trim()) {
      setStatusMessage('Agrega los estudios o examenes solicitados.');
      return;
    }

    setIsSaving(true);
    setStatusMessage('');
    try {
      const response = await auditFetch(`${backendUrl}/api/medical-orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          appointmentId,
          patient,
          doctor,
          orderType,
          requestedStudies,
          clinicalIndications,
          priority,
          relatedDiagnosis,
        }),
      }, user);
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'No se pudo guardar la orden');
      setOrders((current) => [data.order, ...current]);
      setStatusMessage(`Orden guardada. Codigo: ${data.order.verificationCode}. Usa el boton PDF en el historial para imprimir o descargar.`);
    } catch (error) {
      console.error('Error saving medical order:', error);
      setStatusMessage(error.message || 'No se pudo emitir la orden.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="digital-prescription-panel">
      <div className="clinical-files-header">
        <div>
          <h3>Ordenes de laboratorio e imagen</h3>
          <p>Solicitudes medicas digitales con prioridad, diagnostico y PDF imprimible.</p>
        </div>
        <span>{orders.length} orden(es)</span>
      </div>

      <div className="prescription-medication-card">
        <div className="prescription-grid">
          <select value={orderType} onChange={(event) => setOrderType(event.target.value)}>
            <option value="laboratorio">Laboratorio</option>
            <option value="imagen">Imagen</option>
          </select>
          <select value={priority} onChange={(event) => setPriority(event.target.value)}>
            <option value="rutina">Rutina</option>
            <option value="preferente">Preferente</option>
            <option value="urgente">Urgente</option>
          </select>
          <input value={relatedDiagnosis} onChange={(event) => setRelatedDiagnosis(event.target.value)} placeholder="Diagnostico relacionado" />
        </div>
        <textarea
          value={requestedStudies}
          onChange={(event) => setRequestedStudies(event.target.value)}
          placeholder={orderType === 'imagen' ? 'Ej. Radiografia de torax PA y lateral' : 'Ej. Biometria hematica, glucosa, creatinina'}
          rows={3}
        />
        <textarea
          value={clinicalIndications}
          onChange={(event) => setClinicalIndications(event.target.value)}
          placeholder="Indicaciones clinicas, preparacion, ayuno, contraste, lateralidad o motivo del estudio"
          rows={3}
        />
      </div>

      <button type="button" className="btn btn-primary full-width" onClick={saveOrder} disabled={isSaving}>
        {isSaving ? 'Guardando...' : 'Guardar orden'}
      </button>
      {statusMessage && <p className="clinical-files-message">{statusMessage}</p>}

      {orders.length > 0 && (
        <div className="prescription-history">
          <strong>Ordenes emitidas</strong>
          {orders.slice(0, 4).map((order) => (
            <div key={order.id} className="prescription-history-item">
              <span>
                {new Date(order.createdAt).toLocaleString('es-EC')} · {orderTypeLabels[order.orderType] || 'Orden'} · {priorityLabels[order.priority] || order.priority}
              </span>
              <button type="button" onClick={() => openPdf(order)}>PDF</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MedicalOrdersPanel;
